  import { useMutation } from '@apollo/client';
  import { Box, ButtonGroup, Flex, Stack } from '@chakra-ui/react';
  import React, { useState, useEffect, useRef, useCallback } from 'react';

  import LoadingBar from 'react-top-loading-bar';
  import { useRecoilState, useRecoilValue } from 'recoil';
  import { useSchemaMutation } from '../../react-apollo-introspection';
  import { authAtom } from '../../state/auth';
  import TableComponent from '../Table';
  import Loading from '../Table/Loading';
  import { tableAtom, tableFilterAtom } from './atoms';
  import CustomTitleSearch from './CustomTitleSearch';
  import SearchInput from './SearchInput';
  import TableActions from './TableActions';
  import TableFilters from './TableFilters';

  import CustomSearch from './CustomSearch';
  import CustomPlaceSearch from './CustomPlaceSearch';
  import GenericCustomSearch from './GenericCustomSearch';

  import TableSort from './TableSort';
  import { useSyncTableQueryParams } from './hooks/useSyncTableQueryParams';
  import { useNavigate, useSearchParams } from 'react-router-dom';
import OperatorSearchSelect from './OperatorSearchSelect';

  const ListView = ({
    locationFilter,
    setup,
    searchForPlace,
    columns,
    handleRowSelection,
    gqlDelete,
    filters,
    defaultFilter,
    searchString,
    renderRowSubComponent,
    fetchData = {},
    setExternalSearch,
    fetchFilters,
    setSearchParam,
    customFetchFilters,
    setFilterDrawer,
    setSearchString,
    filterKey,
    loading,
    customVariables,
    data: _data = [],
    error,
  }) => {
    useSyncTableQueryParams(setup.model);
    const ref = useRef(null);
    const currentUser = useRecoilValue(authAtom);
    const [selected, setSelected] = useState([]);
    const [tableState, setTableState] = useRecoilState(tableAtom);
    const [dataFilters, setFilters] = useRecoilState(tableFilterAtom);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    console.log("List view data", _data)

    const data = _data?.items;
    const meta = _data?.pageInfo;

    useEffect(() => {
      if(setup.model === tableState.hydrated) {
        fetchData({
          variables: {
            regexp: tableState.regexp,
            filter: tableState.filter,
            sort: tableState.params.sort,
            perPage: tableState.params.pagination,
            skip: tableState.params.skip,
            limit: tableState.params.pagination,
            page: tableState.params.page,
            ...fetchFilters,
            ...customVariables,
          },
          fetchPolicy: 'network-only',
        });
      }
    }, [
      tableState.hydrated,
      fetchData,
      fetchFilters,
      currentUser,
      tableState.params.sort,
      tableState.params.pagination,
      tableState.regexp,
      tableState.params.limit,
      tableState.filter,
      tableState.filter?.OR,
      tableState.params.skip,
      tableState.params.page,
    ]);

    useEffect(() => {
      if (loading && !data) {
        ref.current.continuousStart();
      }
      if ((!loading && data) || error) {
        ref.current.complete();
      }
    }, [loading]);


    const handleRowSelect = useCallback((rows) => {
      const flat = rows ? rows.map((d) => d.original[setup.accessor]) : [];
      setSelected((old) => {
        if (old != flat) {
          setSelected(flat);
        }
      });
      setTableState((old) => ({
        ...old,
        selected: flat,
        selectedRows:
          rows && rows.length !== 0 ? rows.map((d) => d.original) : [],
      }));
      if (handleRowSelection) {
        handleRowSelection(rows);
      }
    }, []);


    const [deleteItems] = useSchemaMutation(gqlDelete);

    const onDelete = () => {
      deleteItems({
        variables: { _ids: tableState.selected },
        onCompleted: () => window.location.reload(),
      });
    };

    /// SEARCHING

    const changeSearch = (value) => {
      setFilters((old) => ({ ...old, search: value }));
    };

    const searchPlace = (value) => {
      setFilters((old) => ({ ...old, placeId: value }));
    };

  const onChangePageSize = useCallback(
    (limit) => {
      setTableState((old) => ({
        ...old,
        params: {
          ...old.params,
          pagination: parseInt(limit),
          page: 1, // reset to page 1 on page size change
          skip: 0,
        },
      }));
      searchParams.set('limit', limit);
      searchParams.set('page', 1);
      navigate(`?${searchParams.toString()}`);
    },
    [setTableState, searchParams, navigate]
  );

  const onChangePage = useCallback(
    (page) => {
      setTableState((old) => ({
        ...old,
        params: { ...old.params, page, skip: (page - 1) * old.params.pagination },
      }));
      searchParams.set('page', page);
      navigate(`?${searchParams.toString()}`);
    },
    [setTableState, searchParams, navigate]
  );

    return (
      <Flex w='100%' direction='column' minHeight='500px'>
        <Box pos='fixed' zIndex={9991} w='100%' left={0} top={0}>
          <LoadingBar color='#366DDD' ref={ref} />
        </Box>
        <Flex align='center' p='10px' zIndex={1} pos='relative'>
          <Loading loading={loading} />
          <Stack isInline spacing='10px' w='fit-content'>
            {setup.canSearch && <SearchInput changeSearch={changeSearch} />}
            <ButtonGroup
              width='100%'
              size='md'
              isAttached
              variant='outline'
              colorScheme='gray'
            >
              {/* <OperatorSearchSelect
                setup={setup}
                filters={filters}
                setFilters={setFilters}
                defaultFilter={defaultFilter}
              /> */}
              {setSearchString && (
                <CustomTitleSearch setSearchString={setSearchString} />
              )}
              {locationFilter && <CustomSearch filters={filters} />}
              {searchForPlace && (
                <CustomPlaceSearch
                  searchForPlace={searchForPlace}
                  filters={filters}
                />
              )}

              {setExternalSearch && (
                <GenericCustomSearch setExternalSearch={setExternalSearch} />
              )}
              {/* {setFilterDrawer && (
                <TableFilters setFilterDrawer={setFilterDrawer} />
              )} */}
              <TableSort columns={columns} setup={setup} />
              <TableActions
                columns={columns}
                setup={setup}
                onDelete={onDelete}
                selected={selected}
              />
            </ButtonGroup>
          </Stack>
        </Flex>

        <TableComponent
          data={data ? data : []}
          meta={meta}
          columns={columns}
          onRowSelect={
            setup.canSelect === undefined
              ? handleRowSelect
              : setup.canSelect
              ? handleRowSelect
              : null
          }
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
          // onChangePage={(_page) => {
          //   setTableState((old) => ({
          //     ...old,
          //     params: { ...old.params, page: _page },
          //   }));
          // }}
          // onChangePageSize={(_page) => {
          //   setTableState((old) => ({
          //     ...old,
          //     params: { ...old.params, page: _page },
          //   }));
          // }}
          setup={setup}
          accessor={setup.accessor}
          loading={loading}
          error={error}
          renderRowSubComponent={renderRowSubComponent}
        />
      </Flex>
    );
  };

  export default ListView;
